import { MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { getLocations } from '../models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { LinkButton } from '../../../components/button/ButtonLink';
import { Table } from 'components/table/Table';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from 'components/Breadcrumb';
import { ListPage } from 'components/page-templates/ListPage';
import { useDebouncedSearchParams } from 'src/hooks/useDebouncedSearchParams';

const TABLE_HEADERS = [
  {
    id: 'name',
    name: 'Naam',
  },
  {
    id: 'address',
    name: 'Adres',
  },
  {
    id: 'postalCode',
    name: 'Postcode',
  },
  {
    id: 'country',
    name: 'Land',
  },
  {
    id: 'isActive',
    name: 'Is actief?',
  },
];

const LocationsPage = () => {
  const [searchParams, setSearchParams] = useDebouncedSearchParams();
  const [searchValue, _setSearchValue] = useState(() => searchParams['search'] ?? '');
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getLocations(variables.cursor, variables.take, {
        search: searchValue,
      });
    },
    [searchValue],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 20,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  const setSearchValue = useCallback(
    (newValue: string) => {
      setSearchParams({ search: newValue });
      _setSearchValue(newValue);
    },
    [_setSearchValue],
  );

  useEffect(() => {
    page.reset();
  }, [searchValue]);

  return (
    <>
      <PageHeader title="Locaties" />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={<Breadcrumb currentItem="Locaties" />}
            rightSide={
              <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                Nieuwe locatie
              </LinkButton>
            }
          />
        }
        filters={
          <Input
            type="text"
            placeholder="Zoek een locatie..."
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={<MagnifyingGlassIcon className="input-icon" />}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={TABLE_HEADERS}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(location) => {
            return [
              <Link to={`${location.id}/general`} className="link-text">
                {location.name}
              </Link>,
              `${location.street} ${location.streetNumber}`,
              location.postalCode,
              location.country,
              location.deactivationReason ? 'Niet actief' : 'Actief',
            ];
          }}
        />
      </ListPage>
    </>
  );
};

export default LocationsPage;
